import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { tokenStorage } from "../utils/storage";
import { API_URL } from "./env";
import { loadingManager } from "../utils/loadingManager";
import { store } from "../store";

class BaseApi {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor(baseURL?: string) {
    this.axiosInstance = axios.create({
      baseURL: baseURL || API_URL,
      withCredentials: true,
    });

    this.initInterceptors();
  }

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  private initInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        loadingManager.startLoading();
        const token = tokenStorage.get();
        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
        }
        return config;
      },
      (error) => {
        loadingManager.stopLoading();
        return Promise.reject(error);
      },
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        loadingManager.stopLoading();
        return response;
      },
      async (error) => {
        loadingManager.stopLoading();

        const originalRequest = error.config as any;
        if (
          originalRequest.url?.includes("/auth/refresh-token") ||
          originalRequest.url?.includes("/auth/login")
        ) {
          return Promise.reject(error);
        }
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return this.axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const res = await this.axiosInstance.post<{ accessToken: string }>(
              "/auth/refresh-token",
              {},
              { withCredentials: true },
            );

            const newAccessToken = res.data.accessToken;
            tokenStorage.set(newAccessToken);
            this.processQueue(null, newAccessToken);

            originalRequest.headers["Authorization"] =
              `Bearer ${newAccessToken}`;
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            tokenStorage.clear();
            store.dispatch({ type: "auth/logout/fulfilled" });
            window.location.href = "/login";
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }
        return Promise.reject(error);
      },
    );
  }

  async GET<T = any>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.axiosInstance.get<T>(url, { params, ...config });
    return res.data;
  }

  async POST<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.axiosInstance.post<T>(url, data, { ...config });
    return res.data;
  }

  async PUT<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.axiosInstance.put<T>(url, data, { ...config });
    return res.data;
  }

  async DELETE<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.axiosInstance.delete<T>(url, { ...config });
    return res.data;
  }
  async PATCH<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.axiosInstance.patch<T>(url, data, { ...config });
    return res.data;
  }
}

const ApiClient = new BaseApi();
export { BaseApi, ApiClient };
